import { FC } from "react";

import { Box, Stack, useTheme } from "@mui/material";
import { UseFormReturn } from "react-hook-form";

import { DashboardTranslationKey } from "../../../../../../../../public/@types/translations";
import { AmplitudeEvents } from "../../../../../../../common/constants/amplitude";
import { useTranslate } from "../../../../../../../common/hooks/useTranslate";
import { LiveEditorToggle } from "../../../../../../components/LiveEditorElements/LiveEditorToggle/LiveEditorToggle";
import { useLiveFormAmplitudeEvents } from "../../../../common/amplitude/useLiveFormAmplitudeEvents";
import { useLiveFormEditorContext } from "../../../../common/LiveFormEditorContext";
import { FrequencyAmountsName, FrequencySwitchName, StepSuggestedDonationAmountsFormData } from "../../types";
import { AmountsInputsArray } from "../AmountsInputsArray/AmountsInputsArray";

import { DashboardTooltip } from "@/dashboard-design-system/DashboardTooltip/DashboardTooltip";
import { DashboardTypography } from "@/dashboard-design-system/Typography";

interface FrequencyRowProps {
  form: UseFormReturn<StepSuggestedDonationAmountsFormData>;
  name: FrequencySwitchName;
  amountsName: FrequencyAmountsName;
  labelKey: DashboardTranslationKey;
  "data-test"?: string;
  placeholders: DashboardTranslationKey[];
  locale: ReturnType<typeof useLiveFormEditorContext>["apiFormObject"]["locale"];
  isEnabled: boolean;
  isToggleDisabled?: boolean;
  onSwitchChange: (checked: boolean) => void;
  amplitudeEventAdded: AmplitudeEvents;
  amplitudeEventRemoved: AmplitudeEvents;
}

export const FrequencyRow: FC<FrequencyRowProps> = ({
  form,
  name,
  amountsName,
  labelKey,
  "data-test": dataTest,
  placeholders,
  locale,
  isEnabled,
  isToggleDisabled = false,
  onSwitchChange,
  amplitudeEventAdded,
  amplitudeEventRemoved,
}) => {
  const { t } = useTranslate();
  const theme = useTheme();
  const { logUserActionEvent } = useLiveFormAmplitudeEvents();

  return (
    <Box
      sx={{
        width: "100%",
        paddingLeft: 1,
        paddingRight: 2,
        paddingY: 1.25,
        borderRadius: 1,
        border: `1px solid ${theme.palette.border.neutral.quiet}`,
        containerType: "inline-size",
      }}
    >
      <Stack
        direction="column"
        alignItems="stretch"
        spacing={1}
        flexWrap="wrap"
        useFlexGap
        sx={{
          width: "100%",
          minHeight: 0,
          "@container (min-width: 450px)": {
            flexDirection: "row",
            alignItems: "center",
            columnGap: 3,
            minHeight: 38,
          },
        }}
      >
        <DashboardTooltip
          title={t("dashboard", "liveFormEditor.stepSuggestedDonationAmounts.maxFrequenciesTooltip")}
          disabled={!isToggleDisabled}
          placement="right"
          slotProps={{ tooltip: { sx: (theme) => theme.typography.caption } }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              minWidth: 128,
            }}
          >
            <LiveEditorToggle
              label={t("dashboard", labelKey)}
              control={form.control}
              name={name}
              hideLabel
              disabled={isToggleDisabled}
              data-test={dataTest}
              onValueChange={(checked) => {
                logUserActionEvent(checked ? amplitudeEventAdded : amplitudeEventRemoved);
                onSwitchChange(checked);
              }}
            />
            <DashboardTypography
              variant="body1"
              vibe="text-neutral-moderate"
              sx={{ whiteSpace: "nowrap", opacity: isToggleDisabled ? 0.4 : 1 }}
            >
              {t("dashboard", labelKey)}
            </DashboardTypography>
          </Box>
        </DashboardTooltip>
        <Box
          sx={{
            display: isEnabled ? "block" : "none",
            flex: 1,
            minWidth: 0,
            "@keyframes frequency-amounts-in": { from: { opacity: 0 }, to: { opacity: 1 } },
            animation: "frequency-amounts-in 300ms ease-in-out",
          }}
        >
          <Box
            sx={{
              marginTop: 0.5,
              "@container (min-width: 500px)": { marginTop: 0 },
            }}
          >
            <AmountsInputsArray
              inputsArray={form.getValues()[amountsName]}
              form={form}
              locale={locale}
              name={amountsName}
              placeholders={placeholders}
            />
          </Box>
        </Box>
      </Stack>
    </Box>
  );
};
